import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { SectionHeading, GoldButton } from "@/components/Brand";
import QrInstall from "@/components/QrInstall";
import EventCard from "@/components/EventCard";
import BeforeAfter from "@/components/BeforeAfter";
import SmartImage from "@/components/SmartImage";
import { FramedImage } from "@/components/SmartImage";

const HOME_PORTRAITS = [
  "/portfolio/home-retrato-1.png",
  "/portfolio/home-retrato-2.png",
  "/portfolio/home-retrato-3.webp",
  "/portfolio/home-retrato-4.webp",
];

const SERVICE_KEYS = [  ["event", "Fotografía de Eventos", "Event Photography"],
  ["portrait", "Sesiones de Retrato", "Portrait Sessions"],
  ["graduation", "Fotografía de Graduación", "Graduation Photography"],
  ["reunion", "Fotografía de Reuniones", "Reunion Photography"],
  ["family", "Fotografía Familiar", "Family Photography"],
  ["digital", "Paquetes Digitales", "Digital Photo Packages"],
  ["print", "Paquetes de Impresión", "Print Packages"],
];

export default function Home() {
  const { t, path, lang } = useI18n();
  const [events, setEvents] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [siteHome, setSiteHome] = useState({ portrait_images: [], featured_images: [] });
  const [restorations, setRestorations] = useState([]);

  useEffect(() => {
    api.get("/restorations").then((r) => setRestorations(r.data.slice(0, 3))).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/events", { params: { featured: true } }).then((r) => setEvents(r.data)).catch(() => {});
    api.get("/portfolio").then((r) => setPortfolio(r.data)).catch(() => {});
    api.get("/site/home").then((r) => setSiteHome(r.data)).catch(() => {});
  }, []);

  const portraits = siteHome.portrait_images?.length ? siteHome.portrait_images : HOME_PORTRAITS;
  const featuredImages = siteHome.featured_images?.length
    ? siteHome.featured_images
    : portfolio.flatMap((p) => (p.images || []).slice(0, 2)).slice(0, 8);

  return (
    <div>
      <section className="relative grain overflow-hidden border-b border-[#C69A3B]/30">
        <div className="mx-auto grid max-w-[1500px] items-center gap-10 px-4 py-14 sm:px-8 lg:grid-cols-[1fr_1.05fr] lg:gap-16 lg:px-12 lg:py-24">
          <div className="reveal">
            <img
              src="/hr-creations-logo.png"
              alt="HR Creations"
              className="mb-8 h-28 w-28 rounded-full ring-1 ring-[#C69A3B]/50 sm:h-36 sm:w-36"
            />
            <p className="label-mono">{t("brand.positioning")}</p>
            <h1 className="mt-5 font-serif-display text-4xl tracking-tight text-[#8F3329] sm:text-5xl lg:text-6xl">
              HR Creations
            </h1>
            <p className="mt-3 font-serif-display text-2xl italic text-[#C69A3B]">{t("brand.tagline")}</p>
            <div className="gold-rule my-7 w-40" />
            <p className="text-base text-[#252525]">{t("hero.line")}</p>
            <p className="mt-4 max-w-md font-serif-display text-xl leading-relaxed text-[#5A5752] sm:text-2xl">
              {t("hero.message")}
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <GoldButton as={Link} to={path("events")} data-testid="hero-view-events-btn">
                {t("btn.viewEvents")}
              </GoldButton>
              <GoldButton as={Link} to={path("clientGalleries")} variant="outline" data-testid="hero-client-galleries-btn">
                {t("btn.clientGalleries")}
              </GoldButton>
              <GoldButton as={Link} to={path("services")} variant="ghost" data-testid="hero-services-btn">
                {t("btn.services")} →
              </GoldButton>
            </div>
          </div>
          <div className="reveal relative" style={{ animationDelay: "120ms" }}>
            <div className="photo-hover overflow-hidden border border-[#C69A3B]/40">
              <img
                src="/hector-rivera-v3.png"
                alt={t("about.name")}
                className="w-full object-contain"
                data-testid="hero-portrait"
              />
            </div>
            <div className="mt-5 border border-[#C69A3B] bg-[#FAFAF7] px-7 py-5">
              <p className="font-serif-display text-xl tracking-wide text-[#8F3329] sm:text-2xl" data-testid="hero-photographer-name">
                {t("about.name")}
              </p>
              <p className="label-mono mt-1">{t("about.role")}</p>
              <p className="mt-3 font-serif-display text-lg tracking-wide text-[#252525] sm:text-xl">
                Coamo, Puerto Rico
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1500px] px-4 py-16 sm:px-8 lg:px-12 lg:py-24">
        <SectionHeading label={t("brand.tagline")} title={t("home.featuredEvents")} sub={t("home.featuredEventsSub")} />
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8" data-testid="featured-events-grid">
          {events.length === 0 && <p className="text-sm text-[#5A5752]">{t("events.empty")}</p>}
          {events.map((ev) => (
            <EventCard key={ev.id} event={ev} />
          ))}
        </div>
      </section>

      <section className="border-y border-[#C69A3B]/30 bg-[#FAFAF7]">
        <div className="mx-auto max-w-[1500px] px-4 py-16 sm:px-8 lg:px-12 lg:py-24">
          <SectionHeading
            label="Portfolio"
            title={t("home.featuredPhotography")}
            sub={t("home.featuredPhotographySub")}
          />
          <div className="masonry mt-10" data-testid="featured-photography-grid">
            {featuredImages.map((src, i) => (
              <div key={src + i} className="photo-hover overflow-hidden border border-[#C69A3B]/25 bg-white">
                <FramedImage src={src} alt="HR Creations" loading="lazy" className="w-full" />
              </div>
            ))}
          </div>
          <div className="mt-10">
            <GoldButton as={Link} to={path("portfolio")} variant="outline" data-testid="home-portfolio-link">
              {t("portfolio.title")} →
            </GoldButton>
          </div>
        </div>
      </section>

      {restorations.length > 0 && (
        <section className="border-t border-[#C69A3B]/30" data-testid="home-restoration-section">
          <div className="mx-auto max-w-[1500px] px-4 py-16 sm:px-8 lg:px-12 lg:py-24">
            <SectionHeading
              label={lang === "es" ? "Restauración" : "Restoration"}
              title={t("home.restorationTitle")}
              sub={t("home.restorationSub")}
            />
            <div className="mt-10 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {restorations.map((item) => (
                <div key={item.id} data-testid={`home-restoration-${item.id}`}>
                  <BeforeAfter
                    before={item.before_image}
                    after={item.after_image}
                    orientation={item.orientation}
                    testId={`home-compare-${item.id}`}
                  />
                  <p className="mt-4 font-serif-display text-lg text-[#8F3329]">
                    {lang === "es" ? item.title_es : item.title_en || item.title_es}
                  </p>
                </div>
              ))}
            </div>
            <div className="mt-10">
              <GoldButton as={Link} to={path("restoration")} data-testid="home-restoration-link">
                {t("restoration.title")} →
              </GoldButton>
            </div>
          </div>
        </section>
      )}

      <section className="mx-auto grid max-w-[1500px] gap-10 px-4 py-16 sm:px-8 lg:grid-cols-2 lg:items-center lg:px-12 lg:py-24">        <div>
          <SectionHeading label={t("brand.positioning")} title={t("home.aboutTitle")} />
          <p className="mt-6 max-w-xl text-sm text-[#5A5752] sm:text-base">{t("home.aboutText")}</p>
          <Link
            to={path("about")}
            data-testid="home-about-link"
            className="mt-7 inline-flex min-h-[44px] items-center border-b border-[#C69A3B] text-sm text-[#8F3329] transition-colors duration-300 hover:text-[#252525]"
          >
            {t("home.aboutLink")} →
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-4" data-testid="home-portraits-grid">
          {portraits.map((src, i) => (
            <div key={src} className="photo-hover overflow-hidden border border-[#C69A3B]/25">
              <SmartImage
                src={src}
                alt="HR Creations"
                loading="lazy"
                data-testid={`home-portrait-${i}`}
                className="aspect-[3/4] w-full"
              />            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-[#C69A3B]/30 bg-[#FAFAF7]">
        <div className="mx-auto max-w-[1500px] px-4 py-16 sm:px-8 lg:px-12 lg:py-24">
          <SectionHeading label="HR Creations" title={t("home.servicesTitle")} sub={t("home.servicesSub")} />
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="services-preview-grid">
            {SERVICE_KEYS.map(([key, es, en], i) => (
              <article
                key={key}
                data-testid={`service-preview-${key}`}
                className="border border-[#C69A3B]/30 bg-white p-6 transition-colors duration-500 hover:border-[#8F3329]"
              >
                <p className="label-mono">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="mt-3 font-serif-display text-lg text-[#252525]">{lang === "es" ? es : en}</h3>
              </article>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-6">
            <GoldButton as={Link} to={path("services")} data-testid="home-services-link">
              {t("btn.services")}
            </GoldButton>
            <div className="w-full max-w-xl">
              <QrInstall />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
